import React, { Component, PropTypes } from 'react';

import defaultImage from 'assets/img/placeholder-img.jpg';
import syscoIcon from 'assets/img/icon-syscoleaf.png';
import Select from "react-select";
import { getAbsoluteSupc } from 'helpers/ingredientHelper';
import { fetchIngredientCost } from 'actions/ingredientsSearch';
import { formatPrice } from 'components/IngredientsSearch/ingredientsSearchHelper';
import { handleImageError, generateProductImage } from 'components/IngredientsSearch/ingredientsSearchHelper';
import { validateAmount, validateYield } from 'components/Forms/validation';

export const REPLACEMENT_PARAMS = {
    YIELD: 'yield',
    NO_OF_EACHES: 'noOfEaches',
    QUANTITY: 'quantity',
    AMOUNT_UNIT: 'amountUnitTextKey',
    COST_PER_SERVING: 'costPerServing',
    RECIPE_ID: 'recipeId',
    INGREDIENT_ID: 'ingredientId',
    ORDINAL: 'ordinal',
}
const LOADING_KEY = -1;
const EMPTY = '';

class SetQuantity extends Component {

    /**
     * Set replacement params on the parent componenet
     * @param {Number} id recipeId
     * @param {String} property property name to be changed eg: any field in REPLACEMENT_PARAMS
     * @param {Event} e change event
     */
    changeReplacementParams = (id, property, e) => {
        const { setReplacementParams } = this.props;
        switch (property) {
            case REPLACEMENT_PARAMS.AMOUNT_UNIT:
                setReplacementParams(id, property, e.value);
                break;
            case REPLACEMENT_PARAMS.QUANTITY:
                const quantity = e.target.value || '0';
                if (validateAmount(quantity)) {
                    setReplacementParams(id, property, this.formatValue(quantity));
                }
                break;
            case REPLACEMENT_PARAMS.YIELD:
                const yieldValue = e.target.value || '0';
                if (validateYield(yieldValue)) {
                    setReplacementParams(id, property, this.formatValue(yieldValue));
                }
                break;
            default:
                setReplacementParams(id, property, e.target.value);
                break;
        }
    }

    /**
     * remove leading zeros, keep lagging decimal point
     * @param {Number} value value to be formatted
     * @returns {String} formatted value
     */
    formatValue = value => {
        let formatted = `${parseFloat(value)}`;
        if (value.slice(-1) === '.') {
            formatted = `${formatted}.`;
        }
        return formatted;
    }

    /**
     * Fetch cost per serving and update
     * @param {Number} id recipeId
     * @param {Number} amount recipe ingredient quantity
     * @param {String} amountUnitTextKey recipe ingredient amount unit
     * @param {Number} productYield product yield
     * @param {Object} selectedIngredient selected ingredient object
     */
    updateCostPerServing = async (id, amount, amountUnitTextKey, productYield, selectedIngredient) => {
        const { setReplacementParams, dispatch } = this.props;

        if (!amount) {
            setReplacementParams(id, REPLACEMENT_PARAMS.COST_PER_SERVING, 0);
        } else {
            setReplacementParams(id, REPLACEMENT_PARAMS.COST_PER_SERVING, LOADING_KEY);
            const response = await dispatch(fetchIngredientCost({
                ...selectedIngredient,
                amountUnitTextKey,
                amountUsedInRecipe: amount,
                yield: productYield,
            }));
            setReplacementParams(id, REPLACEMENT_PARAMS.COST_PER_SERVING, response && response.costPerServing);
        }
    }

    componentDidMount() {
        const {
            newParams,
            selectedIngredient,
        } = this.props;

        Object.entries(newParams).forEach(([recipeId, { quantity, amountUnitTextKey, yield: productYield }]) =>
            this.updateCostPerServing(recipeId, quantity, amountUnitTextKey, productYield, selectedIngredient));
    }

    renderIngredientField = (value, isSysco) => isSysco ? value || '--' : 'N/A';

    renderIngredient = (ingredient, isReplacement) => {
        const {
            mainCategoryName,
            ingredientName,
            ingredientBrand,
            supc,
            packSizePack,
            packSizeSize,
            averageWtPerCase,
            isSyscoBrand,
            stockDesc,
        } = ingredient || {};
        const isSysco = !!supc;
        const absoluteSupc = isSysco ? getAbsoluteSupc(supc) : null;

        return (<div className={`card-item-info ${isReplacement ? 'selected-item' : EMPTY}`}>
            <span className={`card-item-tag ${isReplacement ? 'selected-item-tag' : EMPTY}`}>
                {isReplacement ? 'selected item' : 'current item'}
            </span>
            <div className={'flex'}>
                <div className={'card-item-img'}>
                    <img
                        alt='Item Image'
                        src={isSysco ? generateProductImage(null, absoluteSupc) : defaultImage}
                        onError={e => handleImageError(e, defaultImage)}
                    />
                </div>
                <div className={'card-item-desc flex flex-col'}>
                    <span className={`card-item-category ${isSyscoBrand ? 'sysco-branded' : EMPTY}`}>
                        {this.renderIngredientField(ingredientBrand, true)}
                        {isSyscoBrand ? <img src={syscoIcon} alt={'sysco brand'} className={'ml-5'} /> : null}
                    </span>
                    <span className={'card-item-name'}>{ingredientName}</span>
                    <span className={'card-item-supc'}>{isSysco ? `supc: ${absoluteSupc}` : EMPTY}</span>
                </div>
            </div>
            <div className={'flex mt-10 st-q-card-attributes'}>
                <div className={'card-item-highlight flex flex-col'}>
                    <span className={'card-item-highlight-title'}>Category</span>
                    <span className={'card-item-highlight-value'}>{this.renderIngredientField(mainCategoryName, isSysco)}</span>
                </div>
                <div className={'card-item-highlight flex flex-col'}>
                    <span className={'card-item-highlight-title'}>Pack/Size</span>
                    <span className={'card-item-highlight-value'}>{this.renderIngredientField(`${packSizePack}/${packSizeSize}`, isSysco)}</span>
                </div>
                <div className={'card-item-highlight flex flex-col'}>
                    <span className={'card-item-highlight-title'}>Avg. Weight</span>
                    <span className={'card-item-highlight-value'}>{this.renderIngredientField(averageWtPerCase, isSysco)}</span>
                </div>
                <div className={'card-item-highlight flex flex-col'}>
                    <span className={'card-item-highlight-title'}>Stock Status</span>
                    <span className={'card-item-highlight-value'}>{this.renderIngredientField(stockDesc, isSysco)}</span>
                </div>
            </div>
        </div>);
    };

    renderRecipe = (key, recipe, params, units, updateCostPerServing) => {
        const {
            recipeId,
            name,
            amount,
            amountUnit,
            yield: oldProductYield,
            noOfEaches,
            ingredientId,
        } = recipe || {};
        const id = `${recipeId}:${ingredientId}`;
        const {
            quantity,
            yield: productYield,
            amountUnitTextKey,
            costPerServing,
        } = params;
        const currentUnit = units.find(item => item.value === amountUnitTextKey) || {};


        return (<tbody key={key}>
            <tr>
                <td>{name}</td>{/* Name */}
                <td>{noOfEaches || '--'}</td>{/* Old no of eaches */}
                <td>{`${amount} ${amountUnit}`}</td>{/* Old Quantity */}
                <td>
                    <div className='std-custom-input-wrapper mini-height flex'>
                        {/* New Quantity */}
                        <input
                            type='text'
                            value={quantity}
                            onChange={e => this.changeReplacementParams(id, REPLACEMENT_PARAMS.QUANTITY, e)}
                            onBlur={() => updateCostPerServing(id, quantity, amountUnitTextKey, productYield)}
                        />
                        {/* New Quantity Unit */}
                        <div className={'min-w-80 ml-5'}>
                            <Select
                                classNamePrefix={'opt-right ss-dropdown'}
                                value={{ ...currentUnit, label: currentUnit.value }}
                                options={units}
                                menuPlacement='top'
                                placeholder='Unit'
                                onChange={e => {
                                    this.changeReplacementParams(id, REPLACEMENT_PARAMS.AMOUNT_UNIT, e)
                                    updateCostPerServing(id, quantity, e.value, productYield);
                                }}
                            />
                        </div>
                    </div>
                </td>
                <td>{`${oldProductYield || 100}`}</td>{/* Old Yield */}
                <td>
                    {/* New Yield */}
                    <div className='std-custom-input-wrapper'>
                        <input
                            type='text'
                            value={productYield}
                            onChange={e => this.changeReplacementParams(id, REPLACEMENT_PARAMS.YIELD, e)}
                            onBlur={() => updateCostPerServing(id, quantity, amountUnitTextKey, productYield)}
                        />
                    </div>
                </td>
                <td>
                    {/* New Price */}
                    {costPerServing === LOADING_KEY ? '...' : formatPrice(costPerServing)}
                </td>
            </tr>
        </tbody>);
    }

    render() {
        const {
            currentIngredient,
            selectedIngredient,
            newParams,
            units,
        } = this.props;
        const usedInRecipes = currentIngredient && currentIngredient.usedInRecipes || [];

        return (
            <div className={'common-overlay-content'}>
                <div className={'flex justify-between'}>
                    {this.renderIngredient(currentIngredient)}
                    {this.renderIngredient(selectedIngredient, true)}
                </div>
                <div>
                    <h4 className={'mt-20'}>Item used in recipes ({usedInRecipes.length})</h4>

                    <div className='editable-table'>
                        <table>
                            <thead>
                                <tr>
                                    <th scope='col' className={'set-q-reci-name'}>RECIPE NAME</th>
                                    <th scope='col' className={'set-q-reci-case'}>APPROX. TOTAL UNITS/CASE</th>
                                    <th scope='col' className={'optional-column'}>USED QTY</th>
                                    <th scope='col' className={'optional-column'}>NEW QTY</th>
                                    <th scope='col'>YIELD (%)</th>
                                    <th scope='col'>NEW YIELD (%)</th>
                                    <th scope='col' className={'text-to-right set-q-cost'}>COST</th>
                                </tr>
                            </thead>
                            {usedInRecipes.map((recipe, index) =>
                                this.renderRecipe(
                                    index,
                                    recipe,
                                    newParams[`${recipe.recipeId}:${recipe.ingredientId}`],
                                    units,
                                    (id, amount, amountUnitTextKey, productYield) => this.updateCostPerServing(id, amount, amountUnitTextKey, productYield, selectedIngredient),
                                ))
                            }
                        </table>
                    </div>


                </div>
            </div>
        );
    }
}

SetQuantity.propTypes = {
    currentIngredient: PropTypes.shape({
        usedInRecipes: PropTypes.object,
    }),
    selectedIngredient: PropTypes.object,
    newParams: PropTypes.array,
    setReplacementParams: PropTypes.func,
    units: PropTypes.object,
    dispatch: PropTypes.func,
};

export default SetQuantity;
